import { undoServiceDelete } from '../../api/hotelinfoApi';
import { deleteHotelSection } from '../../api/hotelinfoApi';
import { hasFeature } from '../../permissions';
import {
  Box,
  Button,
  Grid,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Badge,
  Chip,
} from '@mui/material';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Check, X, Settings as SettingsIcon, DollarSign, Clock, CalendarPlus, CheckCircle, XCircle } from 'lucide-react';
import ServiceFormModal from './ServiceFormModal';
import ServiceSettingsModal from './ServiceSettingsModal';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import DisplayCard from '../ui/Card/DisplayCard';
import DraggableCardWrapper from '../ui/Card/DraggableCardWrapper';
import { useUndoableDelete } from '../../hooks/useUndoableDelete';
import UndoSnackbar from '../ui/Feedback/UndoSnackbar';
import PermissionDialog from '../ui/Modal/PermissionDialog';
import TourBookings from '../ServiceManager/TourBookings';
import useTourNotificationStore from '../../stores/tourNotificationStore';
import socket from '../../socket';
import { useNavigate } from 'react-router-dom';
import ServiceSetting from '../ServiceManager/ServiceSetting';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { reorderServices } from '../../api/hotelinfoApi';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7071';

export default function ServiceSection({
  entries,
  setEntries,
  form,
  setForm,
  open,
  setOpen,
  propertyId,
  fetchEntries,
  allowedFeatures
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletedCardId, setDeletedCardId] = useState(null);

  const [permOpen, setPermOpen] = useState(false);
  const [permMsg, setPermMsg] = useState('');
  const showPerm = (msg) => { setPermMsg(msg); setPermOpen(true); };

  const [activeTab, setActiveTab] = useState('bookings'); // 'services' | 'bookings'

  // Category filter for Services tab
  const [serviceCategory, setServiceCategory] = useState('all');

  const getLocalAF = () => {
    try {
      return JSON.parse(localStorage.getItem('allowedFeatures') || '{}');
    } catch (e) {
      console.warn('[ServiceSection] Failed to parse allowedFeatures from localStorage:', e);
      return {};
    }
  };

  const AF = allowedFeatures || getLocalAF();
  const canAdd = !!(AF?.services?.addService ?? hasFeature('services.addService'));
  const canEdit = !!(AF?.services?.editService ?? hasFeature('services.editService'));
  const canDelete = !!(AF?.services?.deleteService ?? hasFeature('services.deleteService'));

  if (process.env.NODE_ENV !== 'production') {
    console.log('[ServiceSection] permissions ->', { canAdd, canEdit, canDelete, services: AF?.services });
  }

  const {
    undoItem,
    showUndo,
    triggerDelete,
    triggerUndo,
    remainingTime
  } = useUndoableDelete({
    onDeleteApi: (item) => deleteHotelSection('services', item._id, propertyId),
    onUndoApi: (item) => undoServiceDelete(item._id, propertyId),
  });

  const newTourCounts = useTourNotificationStore((s) => s.newBookingCounts);
  const clearNewBookingCount = useTourNotificationStore((s) => s.clearNewBookingCount);
  const incrementNewBookingCount = useTourNotificationStore((s) => s.incrementNewBookingCount);
  const newTourCount = useMemo(() => {
    if (!propertyId) return 0;
    return newTourCounts[propertyId] || 0;
  }, [newTourCounts, propertyId]);

  // Live badge count from sockets (section-level)
  const [liveBadgeCount, setLiveBadgeCount] = useState(0);

  useEffect(() => {
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (activeTab === 'bookings' && pid) {
      clearNewBookingCount(pid); // store
      setLiveBadgeCount(0);      // live
    }
  }, [activeTab, propertyId, clearNewBookingCount]);

  useEffect(() => {
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (!pid || activeTab === 'bookings') return;

    // Ensure websocket connection
    try { if (!socket.connected) socket.connect?.(); } catch (e) { console.error('[ServiceSection] socket.connect error', e); }

    const joinRoom = () => {
      try {
        socket.emit('joinTourBookingsRoom', pid);
        if (process.env.NODE_ENV !== 'production') {
          console.log('[socket][tour] joinTourBookingsRoom', { propertyId: pid, connected: socket.connected, id: socket.id });
        }
      } catch (err) {
        console.warn('[socket] joinTourBookingsRoom emit failed (ServiceSection)', err);
      }
    };

    if (socket.connected) joinRoom();
    socket.on('connect', joinRoom);

    // Counts snapshots (support different payload shapes)
    const onCounts = (p = {}) => {
      const n = Number(p?.pendingCount ?? p?.awaiting ?? p?.count ?? 0);
      if (!Number.isNaN(n)) setLiveBadgeCount(n);
    };

    // Creation events: bump live count; also bump store when not on bookings tab
    const onCreated = (payload = {}) => {
      setLiveBadgeCount((n) => (Number.isFinite(n) ? n + 1 : 1));
      incrementNewBookingCount(pid);
    };

    // Wire both namespaced and legacy events
    socket.on('tour:bookings:counts', onCounts);
    socket.on('tourBookings:counts', onCounts);
    socket.on('tourBooking:created', onCreated);
    socket.on('tour:booking:created', onCreated);
    socket.on('tourBooking:new', onCreated);

    return () => {
      socket.off('connect', joinRoom);

      socket.off('tour:bookings:counts', onCounts);
      socket.off('tourBookings:counts', onCounts);
      socket.off('tourBooking:created', onCreated);
      socket.off('tour:booking:created', onCreated);
      socket.off('tourBooking:new', onCreated);
      // Do not socket.disconnect(); it's shared globally.
    };
  }, [propertyId, activeTab, incrementNewBookingCount]);

  // Derive unique categories from entries
  const serviceCategories = useMemo(() => {
    const set = new Set();
    (entries || []).forEach(e => { if (e?.category) set.add(e.category); });
    return Array.from(set).sort();
  }, [entries]);

  const categoryCounts = useMemo(() => {
    const acc = {};
    (entries || []).forEach(e => {
      const key = e?.category || 'Uncategorized';
      acc[key] = (acc[key] || 0) + 1;
    });
    return acc;
  }, [entries]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const sortServices = useCallback((list = []) => {
    return [...list].sort((a, b) => {
      const pa = typeof a?.priority === 'number' ? a.priority : Number.MAX_SAFE_INTEGER;
      const pb = typeof b?.priority === 'number' ? b.priority : Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      const ca = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const cb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return cb - ca;
    });
  }, []);

  const [orderedEntries, setOrderedEntries] = useState(() => sortServices(entries || []));

  useEffect(() => {
    setOrderedEntries(sortServices(entries || []));
  }, [entries, sortServices]);

  const filteredServices = useMemo(() => {
    const list = orderedEntries || [];
    if (serviceCategory === 'all') return list;
    return list.filter(e => (e?.category || '').toLowerCase() === serviceCategory.toLowerCase());
  }, [orderedEntries, serviceCategory]);

  const canReorder = serviceCategory === 'all' && filteredServices.every((entry) => entry && entry._id);
  const sortableIds = useMemo(
    () => filteredServices.map((entry, idx) => String(entry?._id || entry?.id || `temp-${idx}`)),
    [filteredServices]
  );

  const handleDragEnd = useCallback(({ active, over }) => {
    if (!canReorder) return;
    if (!over || active.id === over.id) return;

    setOrderedEntries((prev) => {
      const oldIndex = prev.findIndex((item) => String(item?._id || item?.id) === active.id);
      const newIndex = prev.findIndex((item) => String(item?._id || item?.id) === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(prev, oldIndex, newIndex).map((entry, index) => ({
        ...entry,
        priority: index,
      }));
      setEntries(reordered);
      const payload = reordered
        .filter((entry) => entry?._id)
        .map((entry) => ({ _id: entry._id, priority: entry.priority }));
      if (propertyId && payload.length) {
        reorderServices(propertyId, payload).catch((err) => {
          console.error('[ServiceSection] Failed to reorder services:', err);
        });
      }
      return reordered;
    });
  }, [canReorder, setEntries, propertyId]);

  const handleCardEdit = useCallback((entry) => {
    setForm(entry);
    setOpen(true);
  }, [setForm, setOpen]);

  // Debug log to inspect what is being fetched from the backend
  const saveService = async (payload) => {
    if (payload._id && !canEdit) {
      console.warn('[ServiceSection] Blocked: edit without permission', { canEdit, AF });
      showPerm('You do not have permission to edit services.');
      return;
    }
    if (!payload._id && !canAdd) {
      console.warn('[ServiceSection] Blocked: add without permission', { canAdd, AF });
      showPerm('You do not have permission to add services.');
      return;
    }

    const method = payload._id ? 'PUT' : 'POST';
    const url = payload._id
      ? `${API_BASE}/api/services/${payload._id}`
      : `${API_BASE}/api/services`;

    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      setOpen(false);
      setForm({});
      fetchEntries();
    } else {
      console.error('Failed to save service');
    }
  };

  const handleSaveSettings = (settings) => {
    console.log('Saving settings:', settings);
    setSettingsOpen(false);
  };

  const renderServiceCard = (entry) => {
    if (!entry) return null;

    // Adapt the entry object for DisplayCard
    const adaptedEntry = {
      ...entry,
      title: entry.name, // for alt text
      photos: (entry.images || []).map(img => {
        const imageUrl = img.url || img;
        return imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`;
      }),
    };
    if (entry.image && adaptedEntry.photos.length === 0) {
        const imageUrl = entry.image;
        adaptedEntry.photos.push(imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`);
    }

    return (
      <DisplayCard
        key={entry._id}
        entry={adaptedEntry}
        onEdit={() => {
          if (!canEdit) {
            showPerm('You do not have permission to edit services.');
            return;
          }
          handleCardEdit(entry);
        }}
        onDelete={() => {
          if (!canDelete) {
            showPerm('You do not have permission to delete services.');
            return;
          }
          setDeletingId(entry._id);
          setConfirmOpen(true);
        }}
        onDuplicate={() => {
          if (!canAdd) {
            showPerm('You do not have permission to add services.');
            return;
          }
          const newEntry = { ...entry, name: `${entry.name} (Copy)` };
          delete newEntry._id;
          setForm(newEntry);
          setOpen(true);
        }}
        isDeleted={deletedCardId === entry._id}
        editAllowed={canEdit}
        deleteAllowed={canDelete}
        duplicateAllowed={canAdd}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'bold', color: 'text.primary', textShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            {entry.name}
          </Typography>
          {entry.features && entry.features.length > 0 && (
            <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  textAlign: 'center'
                }}
              >
                {entry.features.map((f) => String(f)).join(' | ')}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
            {entry.description}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, p: 1, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DollarSign size={16} color="#666" />
              <Typography variant="body2" sx={{ fontWeight: '500' }}>{entry.price ? `${entry.price}` : 'Free'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Clock size={16} color="#666" />
              <Typography variant="body2" sx={{ fontWeight: '500' }}>{entry.duration || 'N/A'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarPlus size={16} color="#666" />
              <Typography variant="body2" sx={{ fontWeight: '500' }}>{entry.bookingRequired ? 'Required' : 'Optional'}</Typography>
            </Box>
          </Box>



          {entry.included && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 1 }}>
                <CheckCircle size={18} color="green" style={{ marginTop: 2 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 400, textAlign: 'left', color: 'text.secondary', lineHeight: 1.6, wordBreak: 'break-word', fontSize: '0.9rem' }}
                >
                  {entry.included}
                </Typography>
              </Box>
            </Box>
          )}

          {entry.notIncluded && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 1 }}>
                <XCircle size={18} color="red" style={{ marginTop: 2 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 400, textAlign: 'left', color: 'text.secondary', lineHeight: 1.6, wordBreak: 'break-word', fontSize: '0.9rem' }}
                >
                  {entry.notIncluded}
                </Typography>
              </Box>
            </Box>
          )}

        </Box>
      </DisplayCard>
    );
  };

  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', py: 2 }}>
      {activeTab === 'services' && (
        <>
          <ServiceFormModal
            key={form?._id || 'new'} // force re-mount when editing different service
            open={open}
            initialData={form}
            onSubmit={saveService}
            onClose={() => setOpen(false)}
            editingId={form._id}
          />
        </>
      )}

      {/* Tabs header */}
      <Box
        sx={{
          mt: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_e, val) => { if (val) setActiveTab(val); }}
          sx={{
            minHeight: 38,
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 36,
              px: 2,
              borderRadius: 999,
              color: 'text.secondary',
            },
            '& .Mui-selected': {
              bgcolor: 'action.selected',
              color: 'text.primary',
            },
          }}
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          <Tab
            value="bookings"
            label={
              (() => {
                const badgeVal = (liveBadgeCount ?? 0) || newTourCount || 0;
                return (
                  <Badge
                    badgeContent={badgeVal}
                    color="error"
                    overlap="circular"
                    showZero={false}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Typography sx={{ fontSize: '0.9rem', px: badgeVal ? 0.5 : 0 }}>Bookings</Typography>
                  </Badge>
                );
              })()
            }
          />
          <Tab value="services" label={<Typography sx={{ fontSize: '0.9rem' }}>Services</Typography>} />
          <Tab value="settings" label={<Typography sx={{ fontSize: '0.9rem' }}>Settings</Typography>} />
        </Tabs>

        <Box sx={{ flex: 1 }} />
        <IconButton
          aria-label="Service settings"
          onClick={() => setActiveTab('settings')}
          size="small"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <SettingsIcon size={18} />
        </IconButton>
      </Box>

      {activeTab === 'services' && (
        <Box
          sx={{
            mt: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={`All (${(entries || []).length})`}
              onClick={() => setServiceCategory('all')}
              color={serviceCategory === 'all' ? 'primary' : 'default'}
              variant={serviceCategory === 'all' ? 'filled' : 'outlined'}
              sx={{ borderRadius: 999 }}
            />
            {serviceCategories.map((c) => (
              <Chip
                key={c}
                label={`${c} (${categoryCounts[c] || 0})`}
                onClick={() => setServiceCategory(c)}
                color={serviceCategory === c ? 'primary' : 'default'}
                variant={serviceCategory === c ? 'filled' : 'outlined'}
                sx={{ borderRadius: 999 }}
              />
            ))}
          </Box>

          <Button
            variant="contained"
            aria-disabled={!canAdd}
            onClick={() => {
              if (!canAdd) { showPerm('You do not have permission to add services.'); return; }
              setForm({});
              setOpen(true);
            }}
            startIcon={<Plus size={18} />}
            sx={{
              backgroundColor: canAdd ? undefined : 'lightgray',
              color: canAdd ? undefined : 'white',
              opacity: canAdd ? 1 : 0.4,
              '&:hover': { backgroundColor: canAdd ? undefined : 'lightgray' },
              pointerEvents: 'auto',
              cursor: canAdd ? 'pointer' : 'not-allowed'
            }}
          >
            Add Service
          </Button>
        </Box>
      )}

      {activeTab === 'bookings' && (
        <Box sx={{ mt: 2 }}>
          <TourBookings propertyId={propertyId} />
        </Box>
      )}

      {activeTab === 'services' && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          {canReorder ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                <Grid container spacing={2} columns={12} justifyContent="center" maxWidth="xl">
                  {filteredServices.map((entry, index) => {
                    const card = renderServiceCard(entry);

                    return (
                      <Grid
                        key={entry._id || index}
                        sx={{
                          gridColumn: {
                            xs: 'span 12',
                            sm: 'span 6',
                            md: 'span 4',
                            lg: 'span 3',
                            xl: 'span 2',
                          },
                          textAlign: 'center',
                        }}
                      >
                        <DraggableCardWrapper
                          id={sortableIds[index]}
                        >
                          {card}
                        </DraggableCardWrapper>
                      </Grid>
                    );
                  })}
                </Grid>
              </SortableContext>
            </DndContext>
          ) : (
            <Grid container spacing={2} columns={12} justifyContent="center" maxWidth="xl">
              {filteredServices.map((entry, index) => (
                <Grid
                  key={entry._id || index}
                  sx={{
                    gridColumn: {
                      xs: 'span 12',
                      sm: 'span 6',
                      md: 'span 4',
                      lg: 'span 3',
                      xl: 'span 2',
                    }
                  }}
                >
                  {renderServiceCard(entry)}
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 'settings' && (
        <Box sx={{ mt: 2 }}>
          <ServiceSetting propertyId={propertyId} />
        </Box>
      )}
      {/* Service settings now navigates to /service-settings page instead of modal */}
      {/* <ServiceSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleSaveSettings}
        propertyId={propertyId}
      /> */}
      <PermissionDialog open={permOpen} onClose={() => setPermOpen(false)} message={permMsg} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Service"
        description="Are you sure you want to delete this service? This action cannot be undone."
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingId(null);
        }}
        onConfirm={() => {
          if (!canDelete) { console.warn('[ServiceSection] Confirm delete blocked by permission'); return; }
          const itemToDelete = entries.find(e => e._id === deletingId);
          setDeletedCardId(deletingId);
          triggerDelete(itemToDelete);
          setConfirmOpen(false);
          setDeletingId(null);
        }}
      />
      {activeTab === 'services' && (
        <UndoSnackbar
          show={showUndo}
          label="Service deleted"
          onUndo={() => {
            setDeletedCardId(null);
            triggerUndo();
            fetchEntries();
          }}
          remainingTime={remainingTime}
        />
      )}
    </Box>
  );
}
